import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type AxiosResponse } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew'
import { paginationCountList } from 'shared/ui/TableNew/constants/constants'
import type { AxiosOptions } from 'shared/api'
import { PaginationTimeoutRetry, RequestLoadData } from 'shared/api/lib'
import type { IParamsNumbers, IResponseNumber, IResponseNumbers } from 'entities/Phone/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RowNumber } from 'entities/Phone/model/Table/RowNumber'
import { tableLimitStore } from 'entities/TableLimit'
import type { IPaginationSourceType } from 'entities/Users/api/types'

export class NumbersListStore {
  private readonly _tableStore: TableStore<RowNumber>
  private readonly _paginationTimeoutRetry: PaginationTimeoutRetry
  private _search = ''
  private _itemsMap: Map<number, Phone> = new Map()
  private _scrollToTOpTrigger = ''
  private _firstLoading = true
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null

  constructor(
    private _storeKey: string,
    private _request: (
      params: IParamsNumbers,
      options?: AxiosOptions
    ) => Promise<AxiosResponse<IResponseNumbers>>,
    private _entities: IEntitiesStore,
    private _paginationSourceType?: IPaginationSourceType
  ) {
    this._tableStore = new TableStore({
      key: this._storeKey,
      useColumnsCache: true,
      element: 'number',
      sortOrder: 'asc',
      sortBy: undefined,
      limit: 10,
      page: 1,
      getLimit: () => tableLimitStore.getLimit(this._paginationSourceType),
      setLimit: (value) => {
        if (this._paginationSourceType) tableLimitStore.setLimit(this._paginationSourceType, value)
      },
    })
    this._paginationTimeoutRetry = new PaginationTimeoutRetry({
      storeKey: this._storeKey,
      minLimit: paginationCountList[0],
      getLimit: () => this._tableStore.limit,
      reduceLimit: (limit) => {
        this._tableStore.setLimit(limit)
      },
      retryLoadData: () => this._requestLoadData.debounceLoadData(),
    })

    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    return this.dispose
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  get paramsWithoutPage(): Omit<IParamsNumbers, 'page'> {
    return {
      limit: this._tableStore.limit,
      query: this._search || undefined,
      sortBy: this._tableStore.sortBy || undefined,
      sortOrder: this._tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IParamsNumbers {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      try {
        const { data } = await this._request(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        runInAction(() => {
          this._itemsMap.clear()
          data.data.forEach((item) => {
            if (item.id) {
              this._setItem(item)
            }
          })
          this._tableStore.setTotal(data.total)
          this._scrollToTOpTrigger = nanoid()
          this._paginationTimeoutRetry.handleSuccess()
        })
      } catch (error) {
        if (!this._paginationTimeoutRetry.handleError(error)) {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        if (!this._paginationTimeoutRetry.isRetrying) {
          this._tableStore.setLoading(false)
        }
        this._firstLoading = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get itemsRows() {
    return this.items.map((item) => new RowNumber(item, this._entities))
  }

  get search() {
    return this._search
  }

  get scrollToTOpTrigger() {
    return this._scrollToTOpTrigger
  }

  get tableStore() {
    return this._tableStore
  }

  get firstLoading() {
    return this._firstLoading
  }

  get loading() {
    return this._tableStore.loading
  }

  private _setItem = (data: IResponseNumber) => {
    runInAction(() => {
      this._itemsMap.set(data.id, new Phone(data, this._entities))
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }
}
